import { AbstractControl, ValidatorFn, Validators } from "@angular/forms";
import { debounceTime, filter } from 'rxjs';
import { AxiosError } from 'axios';
import * as ibantools from 'ibantools';
import { errorResponse } from 'src/types/error-response';
import { BankAccountControls, BankAccountForm, BankAccountFormValue } from "../form.controls";
import { BANK_ACCOUNT_FORM_NAME, FormFactory } from "../form.factory";
import { BankAccount } from "../form.resources";

function getBBANCountryCode(form?: AbstractControl | null): string {
  return form?.get('client.address.country_iso')?.value || 'HU';
}

export function getBankAccountForm(this: FormFactory): BankAccountForm {
  const data = this.data as BankAccount | undefined;

  const form = this.fb.group<BankAccountControls>({
    id: this.fb.control(data?.id ?? (this.formService.isGuest ? new Date().valueOf().toString() : null)),
    account_number: this.fb.control(data?.iban || data?.account_number || '', [Validators.required, Validators.maxLength(255), bankAccountFormatValidator]),
  });

  form.statusChanges
    .pipe(
      debounceTime(1000),
      filter(() => form.valid),
    )
    .subscribe({
      next: async () => {
        try {
          const value = form.value as BankAccountFormValue;

          const electronicFormatted = ibantools.electronicFormatIBAN(value.account_number ?? '')!;
          const iban = ibantools.isValidIBAN(electronicFormatted) ? electronicFormatted : null;

          await this.formService.upsertBankAccount({
            ...value,
            iban,
            account_number: iban ? null : electronicFormatted,
          });
          this.trackValidForm(BANK_ACCOUNT_FORM_NAME);
        } catch (error) {
          console.error('Error while saving bank account', error);
          if (error instanceof AxiosError) {
            if (error.response?.status === 422) {
              const errorData = errorResponse.safeParse(error.response?.data);
              if (errorData.success) {
                Object.entries(errorData.data.errors).forEach(([path, errors]) => {
                  form.get(path)?.setErrors({
                    unknown: errors[0],
                  });
                });
              }
            }
          }
        }
      }
    });

  return form;
}


function bankAccountFormatValidator(control: AbstractControl): ReturnType<ValidatorFn> {
  const value = control.value;
  if (!value) {
    return null;
  }

  let electronicFormatted = ibantools.electronicFormatIBAN(value)!;
  const countryCode = getBBANCountryCode(control.parent?.parent);

  if (electronicFormatted.length === 16 && countryCode === 'HU') {
    electronicFormatted = electronicFormatted.padEnd(24, '0');
  }

  if (
    ibantools.isValidIBAN(electronicFormatted)
    || ibantools.isValidBBAN(electronicFormatted, countryCode)
  ) {
    return null;
  }

  return {
    pattern: true,
  };
}
